import React from "react";
import { Link, useLocation } from "react-router-dom";
import { RefreshCw, TriangleAlert } from "lucide-react";
import { reportCrash } from "../lib/reportCrash";

// A red full-screen stack trace is never the right answer for a shopper. One bad deal (or a
// hiccup in Google Maps) now collapses into a small card they can retry from.
class PageBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, attempt: 0 };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[PageBoundary]", this.props.label || "page", error, info?.componentStack);
    reportCrash({
      message: error?.message || String(error),
      stack: `${error?.stack || ""}\n${info?.componentStack || ""}`,
      label: this.props.label || "page",
    });
  }

  retry = () => this.setState((s) => ({ error: null, attempt: s.attempt + 1 }));

  // Same route, different query (a filter, or the escape-hatch link) counts as a fresh start.
  // Only ever fires while the card is up, so normal filtering never remounts a healthy page.
  componentDidUpdate(prev) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.retry();
  }

  render() {
    if (!this.state.error) {
      return <React.Fragment key={this.state.attempt}>{this.props.children}</React.Fragment>;
    }
    return (
      <div className="py-10" data-testid="page-boundary-fallback">
        <div className="mx-auto max-w-md rounded-2xl border border-border bg-card p-6 text-center">
          <span className="mx-auto h-11 w-11 rounded-xl bg-[#e7f6ec] dark:bg-[#16341f] text-[#0f7a3d] grid place-items-center">
            <TriangleAlert size={20} />
          </span>
          <h2 className="mt-4 text-base md:text-lg font-extrabold text-foreground">
            {this.props.title || "This page didn't load"}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            {this.props.body || "Something on this page tripped it up. Try again — it usually comes straight back."}
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <button
              onClick={this.retry}
              data-testid="page-boundary-retry"
              className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#13913f] text-white font-bold text-sm rounded-full px-5 py-2.5 transition-colors"
            >
              <RefreshCw size={15} /> Try again
            </button>
            <Link
              to="/browse"
              data-testid="page-boundary-browse"
              className="inline-flex items-center gap-2 border border-border text-foreground font-bold text-sm rounded-full px-5 py-2.5 hover:border-[#16a34a] transition-colors"
            >
              Browse all deals
            </Link>
          </div>
          <p className="mt-4 text-xs text-muted-foreground/80 break-words" data-testid="page-boundary-reason">
            {String(this.state.error?.message || this.state.error).slice(0, 160)}
          </p>
        </div>
      </div>
    );
  }
}

export default PageBoundary;

// Copy that fits where the break happened, without shipping a boundary per route.
const COPY = [
  [/^\/lytemap|^\/spot\//, "The map didn't load",
    "One of the deals on screen tripped it up. Try again — it usually comes straight back."],
  [/^\/admin/, "This panel didn't load", "Try again. If it keeps failing, check the backend log."],
  [/^\/post-deal|^\/edit-deal/, "The deal form didn't load",
    "Nothing you typed was posted. Try again and it should come back."],
  [/^\/account|^\/analytics/, "This page didn't load",
    "Your account is fine — only the screen failed to draw. Try again."],
];

// Remounting on every navigation is the point: a shopper who walks away from the broken page
// should never carry the error card with them.
export const RouteBoundary = ({ children, label }) => {
  const { pathname, search } = useLocation();
  const [, title, body] = COPY.find(([re]) => re.test(pathname)) || [];
  return (
    <PageBoundary key={pathname} resetKey={`${pathname}${search}`} label={label || pathname}
                  title={title} body={body}>
      {children}
    </PageBoundary>
  );
};
